// Radial gauge of the current period's token consumption against the plan
// cap. Complements (rather than duplicates) the Usage page's linear
// `PlanMeter` — this view is glanceable from across the room, the linear
// bar is the precise read.
//
// Implementation is a hand-rolled SVG ring (no Recharts) — RadialBarChart
// would haul in the whole layout engine for one arc, and `stroke-dasharray`
// gives full control over the threshold-driven color + entrance animation.
//
// Edge cases:
//   - `limits === null`         → tenant has no subscription. Show a CTA
//                                 toward /billing/upgrade rather than a
//                                 0%-of-nothing gauge.
//   - `monthlyTokenCap === null`→ Pro tier (unlimited). Show an infinity
//                                 glyph, neutral ring, no threshold colors.
//   - `pct > 100`               → over-cap. Visual ring locks at 100% but
//                                 the centered number reports the real %
//                                 in destructive red.

import { Link } from 'react-router-dom';
import { Infinity as InfinityIcon, Sparkles } from 'lucide-react';
import type { UsageSummary } from '@cortex/shared';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card.js';
import { Button } from '../ui/button.js';
import { daysUntil } from '../../lib/usage-helpers.js';
import { cn } from '../../lib/utils.js';

interface TokenCapacityCardProps {
  summary: UsageSummary;
}

const RING_SIZE = 120;
const RING_STROKE = 10;
const RING_RADIUS = (RING_SIZE - RING_STROKE) / 2;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

export function TokenCapacityCard({ summary }: TokenCapacityCardProps) {
  const { limits, period, plan } = summary;

  // No-subscription tenants land here in dev too. Surface a clear CTA
  // rather than an empty ring — a "0% of 0" gauge is meaningless.
  if (!limits) {
    return (
      <Card className="flex h-full flex-col">
        <CardHeader>
          <CardTitle>Token capacity</CardTitle>
          <CardDescription>Choose a plan to track usage against a quota.</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-1 flex-col items-center justify-center gap-4 text-center">
          <div
            aria-hidden
            className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary"
          >
            <Sparkles className="h-6 w-6" />
          </div>
          <p className="max-w-xs text-sm text-muted-foreground">
            Free, Starter, and Pro plans each carry a monthly token allowance.
          </p>
          <Button asChild size="sm">
            <Link to="/billing/upgrade">Choose a plan</Link>
          </Button>
        </CardContent>
      </Card>
    );
  }

  const totalTokens = period.inputTokens + period.outputTokens;
  const cap = limits.monthlyTokenCap;

  // Pro tier: cap === null means unlimited. The ring becomes a passive
  // chrome and the centerpiece is an infinity glyph; no color shift.
  if (cap === null) {
    return (
      <Card className="flex h-full flex-col">
        <CardHeader>
          <CardTitle>Token capacity</CardTitle>
          <CardDescription>Unlimited tokens this period.</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-1 flex-col items-center justify-between gap-4 pb-6">
          <Ring percent={0} accent="muted">
            <InfinityIcon
              className="h-10 w-10 text-primary"
              aria-label="Unlimited"
            />
          </Ring>
          <div className="space-y-1 text-center text-xs text-muted-foreground">
            <p>
              <span className="tabular-nums text-foreground">
                {totalTokens.toLocaleString()}
              </span>{' '}
              tokens used
            </p>
            <p>Resets in {daysUntil(plan.currentPeriodEnd)} days</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Hard cap path. Threshold colors mirror the linear PlanMeter in
  // usage/page.tsx so the eye gets the same warning at the same %.
  const pctRaw = cap === 0 ? 0 : (totalTokens / cap) * 100;
  const pctClamped = Math.min(100, pctRaw);
  const accent: RingAccent = pctRaw >= 100 ? 'destructive' : pctRaw >= 80 ? 'warning' : 'success';

  return (
    <Card className="flex h-full flex-col">
      <CardHeader>
        <CardTitle>Token capacity</CardTitle>
        <CardDescription>Tokens used this billing period.</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col items-center justify-between gap-4 pb-6">
        <Ring percent={pctClamped} accent={accent}>
          <div className="flex flex-col items-center leading-tight">
            <span
              className={cn(
                'text-3xl font-semibold tabular-nums',
                accent === 'destructive' && 'text-destructive',
              )}
            >
              {pctRaw.toFixed(0)}%
            </span>
            <span className="text-[11px] uppercase tracking-wide text-muted-foreground">
              of plan
            </span>
          </div>
        </Ring>
        <div className="space-y-1 text-center text-xs text-muted-foreground">
          <p>
            <span className="tabular-nums text-foreground">
              {totalTokens.toLocaleString()}
            </span>{' '}
            of{' '}
            <span className="tabular-nums text-foreground">{cap.toLocaleString()}</span>{' '}
            tokens
          </p>
          <p>Resets in {daysUntil(plan.currentPeriodEnd)} days</p>
        </div>
      </CardContent>
    </Card>
  );
}

type RingAccent = 'success' | 'warning' | 'destructive' | 'muted';

const ACCENT_STROKE: Record<RingAccent, string> = {
  success: 'stroke-success',
  warning: 'stroke-warning',
  destructive: 'stroke-destructive',
  muted: 'stroke-muted-foreground/30',
};

function Ring({
  percent,
  accent,
  children,
}: {
  percent: number;
  accent: RingAccent;
  children: React.ReactNode;
}) {
  const dashOffset = RING_CIRCUMFERENCE * (1 - percent / 100);
  return (
    <div
      className="relative"
      style={{ width: RING_SIZE, height: RING_SIZE }}
      role="img"
      aria-label={`${percent.toFixed(0)} percent of plan capacity`}
    >
      <svg
        width={RING_SIZE}
        height={RING_SIZE}
        viewBox={`0 0 ${String(RING_SIZE)} ${String(RING_SIZE)}`}
        // -90deg rotation places the arc start at 12 o'clock instead of
        // 3 o'clock — reads as "fills clockwise from the top" the way a
        // gauge should.
        style={{ transform: 'rotate(-90deg)' }}
      >
        <circle
          cx={RING_SIZE / 2}
          cy={RING_SIZE / 2}
          r={RING_RADIUS}
          fill="none"
          strokeWidth={RING_STROKE}
          className="stroke-muted"
        />
        <circle
          cx={RING_SIZE / 2}
          cy={RING_SIZE / 2}
          r={RING_RADIUS}
          fill="none"
          strokeWidth={RING_STROKE}
          strokeLinecap="round"
          strokeDasharray={RING_CIRCUMFERENCE}
          strokeDashoffset={dashOffset}
          className={cn(
            'transition-[stroke-dashoffset] duration-500 ease-out',
            ACCENT_STROKE[accent],
          )}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">{children}</div>
    </div>
  );
}
